import BaseAutocomplete from './baseComponents/BaseAutocomplete';
import getPopularCategoryIcon from './utils/getPopularCategoryIcon';

/**
 * A custom element for enhancing a text box into a find autocomplete widget.
 *
 * @module iabbb-find-autocomplete
 */
class FindAutocomplete extends BaseAutocomplete {
  entityIdInputName = 'find_entity';

  grouped = true;

  connectedCallback() {
    this.render();

    const queryParams = new URLSearchParams(window.location.search);

    const searchedQuery = queryParams.get('find_text');

    if (searchedQuery) {
      this.querySelector('input').value = searchedQuery;
      this.showClearButton();
    }
  }

  getOptionAttributes = (result, inputText) => {
    if (result.isPopularCategory) {
      return { html: `${getPopularCategoryIcon(result.title.toLowerCase())}${result.title}` };
    }

    const highlightedTitle =
      inputText.trim().length > 1
        ? result.title.replaceAll(new RegExp(inputText, 'ig'), (match) => `<span>${match}</span>`)
        : result.title;

    if (result.entityType !== 'Organization') {
      return {
        html: highlightedTitle,
        attributes: [{ name: 'data-entity-id', value: result.entityId }],
      };
    }

    const isHq = result.metaTags?.includes('headquarters');

    const isAccreditedBusiness = result.metaTags?.includes('accredited') && !result.metaTags.includes('charity');

    const isAccreditedCharity = result.metaTags?.includes('charityseal') && result.metaTags.includes('charity');

    let html = `<div class="find-ta">`;

    html += `<div class="business-details">`;

    if (isHq) {
      html += `<span class="hq-badge">Headquarters</span>`;
    }

    html += `<div class="business-title">`;
    html += `<span>${highlightedTitle}</span>`;
    html += '</div>';

    if (result.altTitleHighlight) {
      html += `<span class="aka-highlight">also known as ${result.altTitleHighlight}</span>`;
    }

    if (result.secondaryTitle) {
      html += `<div class="business-address">${result.secondaryTitle}</div>`;
    }

    html += '</div>';

    if (isAccreditedBusiness) {
      const businessSealUrl =
        result.country === 'CAN'
          ? 'https://m.bbb.org/terminuscontent/dist/img/business-profile/accreditation/AB-badge-can-v.svg?tx=f_svg'
          : 'https://m.bbb.org/terminuscontent/dist/img/ab-seal-vertical.svg?tx=f_svg';
      html += `<img src="${businessSealUrl}" alt="Accredited Business" width="43" height="64">`;
    }

    if (isAccreditedCharity) {
      const charitySealUrl =
        result.country === 'CAN'
          ? 'https://m.bbb.org/terminuscontent/dist/img/ac-seal-vertical-can.svg?tx=f_svg'
          : 'https://m.bbb.org/terminuscontent/dist/img/ac-seal-vertical.svg?tx=f_svg';
      html += `<img src="${charitySealUrl}" alt="Accredited Charity" width="43" height="64">`;
    }

    html += '</div>';

    return {
      html,
      attributes: [
        { name: 'data-url', value: result.url },
        { name: 'data-title', value: result.title.replace(/<[^>]*>/g, '') },
      ],
    };
  };

  getLegacyOptionAttributes = (result, inputText) => {
    if (result.isPopularCategory) {
      return { html: `${getPopularCategoryIcon(result.title.toLowerCase())}${result.title}` };
    }

    const highlightedTitle =
      inputText.trim().length > 1
        ? result.title.replaceAll(new RegExp(inputText, 'ig'), (match) => `<span>${match}</span>`)
        : result.title;

    if (result.entityType !== 'Organization') {
      return {
        html: highlightedTitle,
        attributes: [{ name: 'data-entity-id', value: result.entityId }],
      };
    }

    const isHq = result.metaTags?.includes('headquarters');

    const isAccreditedBusiness = result.metaTags?.includes('accredited') && !result.metaTags.includes('charity');

    const isAccreditedCharity = result.metaTags?.includes('charityseal') && result.metaTags.includes('charity');

    let html = `<div class="find-ta">`;

    html += '<div>'; // business text container

    html += `<div class="business-title">`;
    html += `<span>${highlightedTitle}</span>`;

    if (isHq) {
      html += `<span class="hq-badge">Headquarters</span>`;
    }

    html += '</div>';

    if (result.altTitleHighlight) {
      html += `<span class="aka-highlight">also known as ${result.altTitleHighlight}</span>`;
    }

    if (result.secondaryTitle) {
      html += `<div class="business-address">${result.secondaryTitle}</div>`;
    }

    html += '</div>'; // close business text container

    if (isAccreditedBusiness) {
      const businessSealUrl =
        result.country === 'CAN'
          ? 'https://m.bbb.org/terminuscontent/dist/img/business-profile/accreditation/AB-Seal-Can-H.svg'
          : 'https://m.bbb.org/terminuscontent/dist/img/ab-seal-horizontal.svg';
      html += `<img src="${businessSealUrl}" alt="Accredited Business" width="91" height="34">`;
    }

    if (isAccreditedCharity) {
      const charitySealUrl =
        result.country === 'CAN'
          ? '/TerminusContent/dist/img/ac-seal-horizontal-can.svg'
          : '/TerminusContent/dist/img/ac-seal-horizontal.svg';
      html += `<img src="${charitySealUrl}" alt="Accredited Charity" width="91" height="34">`;
    }

    html += '</div>';

    return {
      html,
      attributes: [
        { name: 'data-url', value: result.url },
        { name: 'data-title', value: result.title.replace(/<[^>]*>/g, '') },
      ],
    };
  };

  getOptions = (query) => {
    if (query.trim().length === 0) {
      if (!window.headerFooterData) {
        return Promise.resolve([]);
      }

      return window.headerFooterData.bbb.popularCategories
        ? Promise.resolve([
            {
              results: window.headerFooterData.bbb.popularCategories.map((category) => ({
                title: category.name,
                entityId: category.entityId,
                isPopularCategory: true,
              })),
              category: 'Popular Categories',
            },
          ])
        : Promise.resolve([]);
    }

    const country = document.querySelector('[name="find_country"]').value;
    const locationInput = document.querySelector('[name="find_loc"]').value;

    if (query.length > 100) {
      return Promise.resolve([]);
    }

    return fetch(
      `/api/suggest/find?country=${country}&entityTypes=Category&entityTypes=Organization&input=${query}${locationInput ? `&locationInput=${locationInput}` : ''}`,
    )
      .then((res) => {
        if (res.ok) {
          return res.json();
        }

        return res.text().then((msg) => {
          throw msg;
        });
      })
      .then((results) => {
        const groupedResults = results.reduce((acc, result) => {
          const groupLabel = result.entityType === 'Organization' ? 'Businesses' : 'Categories';

          let group = acc.find((x) => x.category === groupLabel);

          if (!group) {
            acc.push({ category: groupLabel, results: [] });
            group = acc[acc.length - 1];
          }

          group.results.push(result);

          return acc;
        }, []);

        return groupedResults;
      })
      .catch((error) => {
        // biome-ignore lint/suspicious/noConsole: propagate error to Sentry
        console.error(error);
        return Promise.resolve([]);
      });
  };
}

customElements.define('iabbb-find-autocomplete', FindAutocomplete);
